import 'helpers/initialLocation';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from 'store/store';
import App from './App';

const container = document.getElementById('root')!;
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <App />
    </Provider>
  </React.StrictMode>
);

// Set up auth timeout logic after React has rendered
// This ensures it doesn't interfere with K8s probes or static file serving
const AUTH_REFRESH_COUNT_KEY = 'auth-refresh-count';
const AUTH_LAST_RELOAD_KEY = 'auth-last-reload';
const MAX_REFRESH_COUNT = 1; // Only allow ONE auto-refresh
const AUTH_TIMEOUT_MS = 5000;
const MIN_TIME_BETWEEN_RELOADS_MS = 3000; // Must be at least 3 seconds between reloads

const initAuthTimeout = () => {
  // Only run in browser environment with sessionStorage available
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return;
  }

  try {
    const refreshCount = parseInt(sessionStorage.getItem(AUTH_REFRESH_COUNT_KEY) || '0', 10);
    const lastReload = parseInt(sessionStorage.getItem(AUTH_LAST_RELOAD_KEY) || '0', 10);
    const timeSinceLastReload = Date.now() - lastReload;

    // Safety check: if we just reloaded very recently, something is wrong - stop immediately
    if (lastReload > 0 && timeSinceLastReload < MIN_TIME_BETWEEN_RELOADS_MS) {
      console.error('Auth reload loop detected - stopping all auto-refresh');
      sessionStorage.removeItem(AUTH_REFRESH_COUNT_KEY);
      sessionStorage.removeItem(AUTH_LAST_RELOAD_KEY);
      return;
    }

    if (refreshCount >= MAX_REFRESH_COUNT) {
      // Max refresh count reached - clear everything
      sessionStorage.removeItem(AUTH_REFRESH_COUNT_KEY);
      sessionStorage.removeItem(AUTH_LAST_RELOAD_KEY);
      return;
    }

    // Increment counter IMMEDIATELY when page loads, not when timeout triggers
    const newCount = refreshCount + 1;
    sessionStorage.setItem(AUTH_REFRESH_COUNT_KEY, newCount.toString());

    const timeoutId = setTimeout(() => {
      // Record timestamp of reload
      sessionStorage.setItem(AUTH_LAST_RELOAD_KEY, Date.now().toString());
      window.location.reload();
    }, AUTH_TIMEOUT_MS);

    // Clear on successful navigation - check if we move past login page
    const observer = new MutationObserver(() => {
      const bodyText = document.body.innerText;
      // Success if we're no longer showing "Loggar in" or we see actual content
      if (!bodyText.includes('Loggar in') && bodyText.length > 50) {
        clearTimeout(timeoutId);
        sessionStorage.removeItem(AUTH_REFRESH_COUNT_KEY);
        sessionStorage.removeItem(AUTH_LAST_RELOAD_KEY);
        observer.disconnect();
      }
    });

    // Start observing
    observer.observe(document.body, { childList: true, subtree: true });
  } catch (error) {
    // Silently fail if sessionStorage is not accessible
    console.error('Failed to initialize auth timeout mechanism:', error);
  }
};

// Initialize after DOM is ready but not interfere with K8s probes
// The key is to run AFTER React render, not before
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAuthTimeout);
} else {
  // DOM already ready, initialize immediately
  initAuthTimeout();
}
